'use client';

import { useState } from 'react';
import Image from 'next/image';
import { projectsData, type Project } from '@/lib/data';
import { PlaceHolderImages } from '@/lib/placeholder-images';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Github, PlayCircle, X, Download } from 'lucide-react'; // Import Download icon
import { cn } from '@/lib/utils';

export default function ProjectsSection({ id }: { id: string }) {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const handleProjectClick = (project: Project) => {
    if (selectedProject?.id === project.id) {
        setSelectedProject(null);
    } else {
        setSelectedProject(project);
    }
  };

  const selectedProjectImage = PlaceHolderImages.find(p => p.id === selectedProject?.imagePlaceholderId);

  return (
    <section id={id} className="py-24 overflow-hidden sm:py-32 bg-primary/5">
      <div className="container px-4 mx-auto sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center lg:max-w-4xl">
          <h2 className="text-4xl font-bold tracking-tight font-headline text-primary sm:text-5xl">
            Gaming Projects
          </h2>
          <p className="mt-4 text-lg text-foreground/80">
            Here are some of the game development projects I've worked on.
          </p>
        </div>

        <div className="mt-16">
          <Carousel
            opts={{
              align: 'start',
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {projectsData.map((project) => {
                 const projectImage = PlaceHolderImages.find(p => p.id === project.imagePlaceholderId);
                 return (
                    <CarouselItem key={project.id} className="md:basis-1/2 lg:basis-1/3">
                        <div className="p-1">
                        <Card 
                            className="overflow-hidden transition-all duration-300 cursor-pointer group hover:shadow-accent/20 hover:shadow-lg"
                            onClick={() => handleProjectClick(project)}
                        >
                            <CardContent className="relative p-0 aspect-video">
                            {projectImage && 
                                <Image
                                    src={projectImage.imageUrl}
                                    alt={project.title}
                                    fill
                                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                                    data-ai-hint={projectImage.imageHint}
                                />
                            }
                            {project.videoUrl && <div className="absolute inset-0 flex items-center justify-center bg-black/40"><PlayCircle className="w-16 h-16 text-white/80"/></div>}
                            </CardContent>
                            <CardFooter className="flex-col items-start p-4">
                                <h3 className="text-lg font-semibold font-headline">{project.title}</h3>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {project.technologies.map(tech => <Badge key={tech} variant="secondary">{tech}</Badge>)}
                                </div>
                            </CardFooter>
                        </Card>
                        </div>
                    </CarouselItem>
                )
              })}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex" />
            <CarouselNext className="hidden sm:flex"/>
          </Carousel>
        </div>

        <div className={cn(
            "mt-12 grid transition-all duration-500 ease-in-out",
            selectedProject ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}>
            <div className="overflow-hidden">
                {selectedProject && (
                    <Card className="flex flex-col md:flex-row">
                    <CardContent className="relative p-0 md:w-1/2 aspect-video">
                        {selectedProject.videoUrl ? (
                        <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={selectedProject.videoUrl}
                            title={selectedProject.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                        ) : selectedProjectImage && (
                        <Image
                            src={selectedProjectImage.imageUrl}
                            alt={selectedProject.title}
                            fill
                            className="object-cover"
                            data-ai-hint={selectedProjectImage.imageHint}
                        />
                        )}
                    </CardContent>
                    <div className="flex flex-col md:w-1/2">
                        <CardHeader>
                        <CardTitle className="text-2xl font-headline">{selectedProject.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                        <p className="text-foreground/80" dangerouslySetInnerHTML={{ __html: selectedProject.longDescription }} />
                        </CardContent>
                        <CardFooter className="gap-4 mt-auto">
                        {selectedProject.playUrl && (
                            <Button asChild>
                            <a href={selectedProject.playUrl} target="_blank" rel="noopener noreferrer">Play Now</a>
                            </Button>
                        )}
                        {selectedProject.repoUrl && (
                            <Button asChild variant="outline">
                                <a href={selectedProject.repoUrl} target="_blank" rel="noopener noreferrer">
                                    <Github className="mr-2"/>
                                    View Code
                                </a>
                            </Button>
                        )}
                        {selectedProject.downloadUrl && (
                            <Button asChild>
                                <a href={selectedProject.downloadUrl} target="_blank" rel="noopener noreferrer" download>
                                    <Download className="mr-2"/>
                                    Download
                                </a>
                            </Button>
                        )}
                        </CardFooter>
                    </div>
                    </Card>
                )}
            </div>
        </div>
      </div>
    </section>
  );
}
